import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const WindowContext = createContext(null);

export const WindowProvider = ({ children }) => {
  const [windows, setWindows] = useState([]);
  const [activeWindowId, setActiveWindowId] = useState(null);

  const openWindow = useCallback((windowData) => {
    const baseTitle = windowData.title || "New Page";
    const pageKey = windowData.pageKey || baseTitle;

    setWindows((prev) => {
      const samePageCount = prev.filter((item) => item.pageKey === pageKey).length;
      const title = samePageCount === 0 ? baseTitle : `${baseTitle}${samePageCount + 1}`;

      const payload = {
        id: windowData.id || `${baseTitle}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        title,
        icon: windowData.icon,
        content: windowData.content,
        minimized: false,
        pageKey,
      };

      setActiveWindowId(payload.id);
      return [...prev, payload];
    });
  }, []);

  const closeWindow = useCallback((id) => {
    setWindows((prev) => {
      const index = prev.findIndex((window) => window.id === id);
      const newWindows = prev.filter((window) => window.id !== id);

      if (activeWindowId === id) {
        const nextWindow = newWindows[index] || newWindows[index - 1] || newWindows[0] || null;
        setActiveWindowId(nextWindow?.id ?? null);
      }

      return newWindows;
    });
  }, [activeWindowId]);

  const minimizeWindow = useCallback((id) => {
    setWindows((prev) => {
      const newWindows = prev.map((window) =>
        window.id === id ? { ...window, minimized: true } : window
      );

      if (activeWindowId === id) {
        const nextVisible = newWindows.find(
          (window) => !window.minimized && window.id !== id
        );
        setActiveWindowId(nextVisible?.id ?? null);
      }

      return newWindows;
    });
  }, [activeWindowId]);

  const restoreWindow = useCallback((id) => {
    setWindows((prev) => prev.map((window) => (window.id === id ? { ...window, minimized: false } : window)));
    setActiveWindowId(id);
  }, []);

  const setActiveWindow = useCallback((id) => {
    setActiveWindowId(id);
  }, []);

  const clearWindows = useCallback(() => {
    setWindows([]);
    setActiveWindowId(null);
  }, []);

  const value = useMemo(
    () => ({
      windows,
      activeWindowId,
      openWindow,
      closeWindow,
      minimizeWindow,
      restoreWindow,
      setActiveWindow,
      clearWindows,
    }),
    [windows, activeWindowId, openWindow, closeWindow, minimizeWindow, restoreWindow, setActiveWindow, clearWindows]
  );

  return <WindowContext.Provider value={value}>{children}</WindowContext.Provider>;
};

export const useWindowContext = () => {
  const context = useContext(WindowContext);
  if (!context) {
    throw new Error('useWindowContext must be used inside a WindowProvider');
  }
  return context;
};
